import { StoreLoader, UnsubscriberClass, IAction, ScrolledByContainerDirective, LanguageService } from '@aston/foundation';
import { BreadcrumbComponent, DebugBarComponent } from '@aston/foundation/components';
import { AIChatBoxComponent, AIChatPanelService } from '@aston/ai-chat';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { ClickOutsideDirective } from '@aston/foundation/directives';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { ActionType, AppLanguage } from '@aston/foundation/enums';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { Component, OnInit, viewChild, inject } from '@angular/core';
import { UserPrefsStoreActions } from '@aston/user-prefs';
import { ThemesModule } from '@aston/themes';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';

import { NotificationsStoreSelectors, NotificationsStoreActions } from '../../../root-store/notifications-store';
import { NotificationsPanelComponent } from '../../components/notifications-panel/notifications-panel.component';
import { SidePanelComponent } from '../../components/side-panel/side-panel.component';
import { AppStoreActions, AppStoreSelectors } from '../../../root-store/app-store';
import { TopBarComponent } from '../../components/top-bar/top-bar.component';
import { UserClearanceLevel } from '../../../authentication-module/enums';
import { MenuComponent } from '../../components/menu/menu.component';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../user-module/services';
import { NotificationCategory } from '../../enums';
import { INotificationItem } from '../../models';

@Component({
	selector: 'master-layout',
	templateUrl: './master-layout.component.html',
	imports: [
		AsyncPipe,
		BreadcrumbComponent,
		ClickOutsideDirective,
		DebugBarComponent,
		MenuComponent,
		NotificationsPanelComponent,
		RouterOutlet,
		ScrolledByContainerDirective,
		AIChatBoxComponent,
		SidePanelComponent,
		ThemesModule,
		TopBarComponent,
	]
})
export class MasterLayoutComponent extends UnsubscriberClass implements OnInit {
	protected aiPanels = inject(AIChatPanelService);
	protected langs = inject(LanguageService);
	protected users = inject(UserService);
	private router = inject(Router);
	private store = inject(Store);

	debugBar = viewChild(DebugBarComponent)
	menuCollapsed = false;

	isAIChatOpen$ = this.store.select(AppStoreSelectors.selectIsAIChatOpen);
	isAIChatOpenOnce$ = this.store.select(AppStoreSelectors.selectIsAIChatOpen).pipe(filter(Boolean), shareReplay(1));

	currentLanguage$ = this.langs.language$
	currentUserAvatar$ = combineLatest([
		this.store.select(AppStoreSelectors.selectCurrentUser),
		this.store.select(AppStoreSelectors.selectCurrentUserAvatar),
	]).pipe(
		filter(([user]) => !!user),
		map(([_, avatar]) => avatar),
	);

	tenant$ = this.store.select(AppStoreSelectors.selectTenant);
	pageOverTitle$ = this.tenant$.pipe(map(tenant => tenant ? tenant.name : ''));
	pageOverLogo$ = this.store.select(AppStoreSelectors.selectTenantLogo);

	isSupport$ = this.store.select(AppStoreSelectors.selectCurrentUserIsSupport);
	hasImportHistoryClearanceLevel: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.HistoryImport));
	hasManualImportClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.ManualImport));
	hasLetteringClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.Lettering));
	hasPaymentOrderClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.PaymentOrder));
	hasAdministrationToolsClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.AdministrationTools));
	hasSuperAdministrationClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.SuperAdministration));
	hasReadOnlyAccessToAllSuperDebtorsClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.ReadOnlyAccessToAllSuperDebtors));
	hasDunningClearanceLevel$: Observable<boolean> = this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.Dunning));

	themesIsOpened = false;
	notificationsIsOpened = false;

	notificationsList$: Observable<INotificationItem[]> = this.store.select(NotificationsStoreSelectors.selectNotifications);
	notificationsAllCount$: Observable<number> = this.store.select(NotificationsStoreSelectors.selectAllCount);
	notificationsHasUnread$: Observable<boolean> = this.store.select(NotificationsStoreSelectors.selectHasUnread);
	notificationsShowUnreadOnly$: Observable<boolean> = this.store.select(NotificationsStoreSelectors.selectShowUnreadOnly);

	notificationsLoader = new StoreLoader(
		this.store.select(NotificationsStoreSelectors.selectNotificationsIsLoading),
		this.store.select(NotificationsStoreSelectors.selectNotificationsError)
	);

	// Use it to stop notifications refresh and avoid redux devtools flooding
	enableNotificationRefresh: boolean = environment.name !== 'Local';

	isHome = false;

	constructor() {
		super();
		const router = this.router;

		router.events.subscribe((e) =>  {
			if (e instanceof NavigationEnd) {
				this.isHome = e.url === "/home" ? true : false
			}
		});
	}

	ngOnInit() {
		this.store.select(NotificationsStoreSelectors.selectIsOpened)
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe(isOpened => this.notificationsIsOpened = isOpened);
	}

	toggleNotificationPanel() {
		this.aiPanels.toggleSideChat(false)
		this.enableNotificationRefresh = true;
		this.store.dispatch(NotificationsStoreActions.ToggleList());

		// let's ask for a notification list right now.
		this.store.dispatch(NotificationsStoreActions.LoadNotificationsRequest());
	}

	toggleThemesPanel() {
		this.aiPanels.toggleSideChat(false)
		setTimeout(_ => {
			this.notificationsIsOpened = false
			this.themesIsOpened = !this.themesIsOpened;
		}, 100)
	}

	handleAction(action: IAction, origin: 'menu' | 'notifications' | 'themes' = action.value) {
		switch (action.type) {
		case ActionType.OPEN:
			this.closePanels()
			break
		case ActionType.TOGGLE:
			if (origin === 'menu') {
				this.menuCollapsed = !this.menuCollapsed;
			} else if (origin === 'notifications') {
				this.toggleNotificationPanel();
			} else if (origin === 'themes') {
				this.toggleThemesPanel();
			}
			break;
		}
	}

	selectLanguage(language: AppLanguage) {
		this.langs.setCurrentLanguage(language)
		firstValueFrom(this.users.updateUserLanguage(language))
	}

	logout() {
		const logoutAction = AppStoreActions.Logout()
		this.store.dispatch(AppStoreActions.OpenConfirmationModal({textsKey: `Modal.ConfirmationDeconnexion.`}, logoutAction));
	}

	closePanels() {
		this.themesIsOpened = false
		if (this.notificationsIsOpened) {
			this.store.dispatch(NotificationsStoreActions.ClosePanel());
		}
	}

	filterNotifications(showUnread: boolean) {
		if (showUnread) {
			this.store.dispatch(NotificationsStoreActions.ShowUnreadOnly());
		} else {
			this.store.dispatch(NotificationsStoreActions.ShowAll());
		}
	}

	selectNotification(notification: INotificationItem) {
		let redirectionRoute = '';
		let redirectionExtras;

		switch (notification.category) {
		case NotificationCategory.None:
			redirectionRoute = '';
			break;
		}

		this.store.dispatch(NotificationsStoreActions.SelectRequest(notification.id, redirectionRoute, redirectionExtras));
	}

	useTheme(theme) {
		this.store.dispatch(UserPrefsStoreActions.UpdatePreferredTheme({theme}))
	}
}

